import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowLeft, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data); setSize(null); setColor(null); setQty(1); setActiveImg(0);
      api.get("/products", { params: { category: r.data.category } }).then((rr) => setRelated(rr.data.filter((p) => p.id !== id).slice(0, 4)));
    }).catch(() => nav("/shop"));
  }, [id, nav]);

  if (!product) return <div className="min-h-[70vh]" />;

  const images = product.images?.length ? product.images : [null];
  const handleAdd = () => {
    if (product.sizes?.length && !size) return toast.error("Please select a size");
    if (size && (product.size_stock?.[size] ?? 0) <= 0) return toast.error("That size is sold out");
    if (product.colors?.length && !color) return toast.error("Please select a color");
    addToCart(product, size, color, qty);
  };

  return (
    <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-10 sm:py-14 min-h-[80vh]">
      <Link to="/shop" className="inline-flex items-center gap-2 label-caps text-muted-foreground hover:text-ink transition-colors mb-8" data-testid="back-to-shop"><ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Back</Link>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* images */}
        <div className="flex flex-col-reverse sm:flex-row gap-4">
          {images.length > 1 && (
            <div className="flex sm:flex-col gap-3">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-16 h-20 overflow-hidden border ${activeImg === i ? "border-ink" : "border-line"}`} data-testid={`thumb-${i}`}>
                  <img src={fileUrl(im)} alt="" className="img-cover" />
                </button>
              ))}
            </div>
          )}
          <motion.div key={activeImg} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="flex-1 overflow-hidden bg-surface aspect-[4/5]">
            {images[activeImg] ? <img src={fileUrl(images[activeImg])} alt={product.name} className="img-cover" /> : <div className="w-full h-full flex items-center justify-center font-serif-display text-muted-foreground">No image</div>}
          </motion.div>
        </div>

        {/* info */}
        <div className="lg:pt-6">
          <p className="label-caps text-muted-foreground mb-3">{product.category}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-light tracking-tight leading-none">{product.name}</h1>
          <p className="font-serif-display text-3xl mt-5">${product.price?.toFixed(0)}</p>
          <p className="text-muted-foreground leading-relaxed mt-6 max-w-md text-sm">{product.description}</p>

          {product.colors?.length > 0 && (
            <div className="mt-8">
              <p className="label-caps mb-3">Color {color && <span className="text-muted-foreground normal-case tracking-normal">— {color}</span>}</p>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`} className={`px-4 py-2 border text-sm transition-colors ${color === c ? "border-ink bg-ink text-pearl" : "border-line hover:border-ink"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {product.sizes?.length > 0 && (
            <div className="mt-6">
              <p className="label-caps mb-3">Size</p>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((s) => {
                  const soldOut = (product.size_stock?.[s] ?? 0) <= 0;
                  return (
                    <button key={s} disabled={soldOut} onClick={() => setSize(s)} data-testid={`size-${s}`}
                      className={`w-12 h-12 border text-sm transition-colors ${size === s ? "border-ink bg-ink text-pearl" : "border-line hover:border-ink"} ${soldOut ? "opacity-40 line-through cursor-not-allowed hover:border-line" : ""}`}>{s}</button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-line">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-12 flex items-center justify-center hover:bg-surface transition-colors" data-testid="qty-minus"><Minus strokeWidth={1.5} className="w-4 h-4" /></button>
              <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(size ? Math.min(product.size_stock?.[size] ?? qty + 1, qty + 1) : qty + 1)} className="w-11 h-12 flex items-center justify-center hover:bg-surface transition-colors" data-testid="qty-plus"><Plus strokeWidth={1.5} className="w-4 h-4" /></button>
            </div>
            <p className="label-caps text-muted-foreground" data-testid="stock-status">
              {size ? ((product.size_stock?.[size] ?? 0) > 0 ? `${product.size_stock[size]} left in ${size}` : "Sold out") : (product.stock > 0 ? "In stock" : "Sold out")}
            </p>
          </div>

          <div className="mt-8 flex gap-3">
            <button onClick={handleAdd} disabled={product.stock <= 0 || (product.sizes?.length > 0 && size && (product.size_stock?.[size] ?? 0) <= 0)} className="flex-1 bg-ink text-pearl py-4 label-caps hover:bg-wine transition-colors disabled:opacity-40" data-testid="add-to-cart-btn">
              {product.stock > 0 ? "Add to bag" : "Sold out"}
            </button>
            <button onClick={() => toggleWishlist(product.id)} className="w-14 border border-line flex items-center justify-center hover:border-ink transition-colors" data-testid="detail-wishlist-btn" aria-label="Save">
              <Heart strokeWidth={1.5} className={`w-5 h-5 ${inWishlist(product.id) ? "fill-wine text-wine" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <h2 className="font-serif-display text-3xl sm:text-4xl font-light mb-10">You may also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
